// Energy Calculators helpers: mechanical energy (KE+PE conservation),
// the work-energy theorem, heat/latent heat, electrical energy and cost,
// mass-energy equivalence, efficiency, escape velocity, an energy unit
// converter, rotational kinetic energy, wind power, and photon energy.
//
// Distinct from lib/tools/dynamicsCalculators.ts, which already has
// standalone Force/Momentum/Impulse/Weight/Friction/Incline/Work/Kinetic
// Energy/Potential Energy/Power/Spring/Torque calculators - this file
// deliberately does NOT duplicate plain KE (½mv²), PE (mgh), or Work (Fd)
// calculators; instead "Mechanical Energy" ties KE+PE together (energy
// conservation) and "Work-Energy Theorem" relates work to a change in KE,
// which are conceptually distinct tools.
import { t } from './i18n'

const G = 6.674e-11 // gravitational constant, m^3 kg^-1 s^-2
const C_LIGHT = 299792458 // speed of light, m/s
const PLANCK_H = 6.62607015e-34 // Planck's constant, J*s
const EV_IN_JOULES = 1.602176634e-19

const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No solution', ru: 'Решения нет', lv: 'Nav risinājuma', pl: 'Brak rozwiązania',
    es: 'Sin solución', fr: 'Pas de solution', it: 'Nessuna soluzione', de: 'Keine Lösung',
}

// ============================================================
// Shared helper: format very large/small numbers in scientific notation
// (e.g. photon energies ~1e-19 J), keeping "normal" magnitudes as plain
// fixed-point numbers so everyday results stay simple to read.
// ============================================================
const SUPERSCRIPT_DIGITS: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻' }
function toSuperscript(text: string): string {
    return text.split('').map((c) => SUPERSCRIPT_DIGITS[c] || c).join('')
}
function formatScientific(n: number, unit: string): string {
    if (n === 0) return `0 ${unit}`
    const exp = Math.floor(Math.log10(Math.abs(n)))
    if (exp > -3 && exp < 7) return `${n.toFixed(4)} ${unit}`
    const mantissa = n / Math.pow(10, exp)
    return `${mantissa.toFixed(4)} × 10${toSuperscript(String(exp))} ${unit}`
}

// ============================================================
// 1. Mechanical Energy Calculator (ME = KE + PE)
// ============================================================
export function mechanicalEnergyCalculator(params: { mass_kg: unknown; velocity_ms: unknown; height_m: unknown; gravity_ms2: unknown; language: unknown }): {
    kinetic_energy_j: number; potential_energy_j: number; mechanical_energy_j: number; steps: string
} {
    const m = Math.max(Number(params.mass_kg) || 0, 0)
    const v = Number(params.velocity_ms) || 0
    const h = Number(params.height_m) || 0
    const g = Number(params.gravity_ms2) || 9.81

    const ke = 0.5 * m * v * v
    const pe = m * g * h
    const me = ke + pe

    const steps = [
        `KE = ½mv² = 0.5 × ${m} × ${v}² = ${ke.toFixed(4)} J`,
        `PE = mgh = ${m} × ${g} × ${h} = ${pe.toFixed(4)} J`,
        `ME = KE + PE = ${ke.toFixed(4)} + ${pe.toFixed(4)} = ${me.toFixed(4)} J`,
    ].join('\n')

    return { kinetic_energy_j: ke, potential_energy_j: pe, mechanical_energy_j: me, steps }
}

// ============================================================
// 2. Work-Energy Theorem Calculator (W_net = KE_f - KE_i)
// ============================================================
export function workEnergyTheoremCalculator(params: { solve_for: unknown; mass_kg: unknown; initial_velocity_ms: unknown; final_velocity_ms: unknown; work_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'work')
    const m = Math.max(Number(params.mass_kg) || 0.0001, 0.0001)
    const vi = Number(params.initial_velocity_ms) || 0
    const vf = Number(params.final_velocity_ms) || 0
    const w = Number(params.work_j) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'final_velocity') {
        const disc = vi * vi + (2 * w) / m
        if (disc < 0) return { result: errText, steps: errText }
        const result = Math.sqrt(disc)
        return { result, steps: `vf = √(vi² + 2W/m) = √(${vi}² + 2×${w}/${m}) = ${result.toFixed(4)} m/s` }
    }
    if (solveFor === 'initial_velocity') {
        const disc = vf * vf - (2 * w) / m
        if (disc < 0) return { result: errText, steps: errText }
        const result = Math.sqrt(disc)
        return { result, steps: `vi = √(vf² - 2W/m) = √(${vf}² - 2×${w}/${m}) = ${result.toFixed(4)} m/s` }
    }
    const keI = 0.5 * m * vi * vi
    const keF = 0.5 * m * vf * vf
    const result = keF - keI
    return { result, steps: `W = ΔKE = ½m(vf² - vi²) = 0.5 × ${m} × (${vf}² - ${vi}²) = ${result.toFixed(4)} J` }
}

// ============================================================
// 3. Heat Energy / Specific Heat Calculator (Q = mcΔT)
// ============================================================
export function heatEnergyCalculator(params: { solve_for: unknown; mass_kg: unknown; specific_heat_jkgc: unknown; delta_t_c: unknown; heat_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'heat')
    const m = Number(params.mass_kg) || 0
    const c = Number(params.specific_heat_jkgc) || 0
    const dt = Number(params.delta_t_c) || 0
    const q = Number(params.heat_j) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (c === 0 || dt === 0) return { result: errText, steps: errText }
        const result = q / (c * dt)
        return { result, steps: `m = Q / (cΔT) = ${q} / (${c}×${dt}) = ${result.toFixed(4)} kg` }
    }
    if (solveFor === 'specific_heat') {
        if (m === 0 || dt === 0) return { result: errText, steps: errText }
        const result = q / (m * dt)
        return { result, steps: `c = Q / (mΔT) = ${q} / (${m}×${dt}) = ${result.toFixed(4)} J/(kg·°C)` }
    }
    if (solveFor === 'delta_t') {
        if (m === 0 || c === 0) return { result: errText, steps: errText }
        const result = q / (m * c)
        return { result, steps: `ΔT = Q / (mc) = ${q} / (${m}×${c}) = ${result.toFixed(4)} °C` }
    }
    const result = m * c * dt
    return { result, steps: `Q = mcΔT = ${m} × ${c} × ${dt} = ${result.toFixed(4)} J` }
}

// ============================================================
// 4. Latent Heat Calculator (Q = mL)
// ============================================================
export function latentHeatCalculator(params: { solve_for: unknown; mass_kg: unknown; latent_heat_jkg: unknown; heat_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'heat')
    const m = Number(params.mass_kg) || 0
    const l = Number(params.latent_heat_jkg) || 0
    const q = Number(params.heat_j) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'mass') {
        if (l === 0) return { result: errText, steps: errText }
        const result = q / l
        return { result, steps: `m = Q / L = ${q} / ${l} = ${result.toFixed(4)} kg` }
    }
    if (solveFor === 'latent_heat') {
        if (m === 0) return { result: errText, steps: errText }
        const result = q / m
        return { result, steps: `L = Q / m = ${q} / ${m} = ${result.toFixed(4)} J/kg` }
    }
    const result = m * l
    return { result, steps: `Q = m × L = ${m} × ${l} = ${result.toFixed(4)} J` }
}

// ============================================================
// 5. Electrical Energy & Cost Calculator (E = Pt)
// ============================================================
export function electricalEnergyCalculator(params: { power_w: unknown; time_hours: unknown; rate_per_kwh: unknown; language: unknown }): {
    energy_kwh: number; energy_j: number; cost: number; steps: string
} {
    const p = Math.max(Number(params.power_w) || 0, 0)
    const th = Math.max(Number(params.time_hours) || 0, 0)
    const rate = Math.max(Number(params.rate_per_kwh) || 0, 0)

    const energyKwh = (p / 1000) * th
    const energyJ = p * th * 3600
    const cost = energyKwh * rate

    const steps = [
        `Energy (kWh) = (P / 1000) × t = (${p} / 1000) × ${th} = ${energyKwh.toFixed(4)} kWh`,
        `Energy (J) = P × t(seconds) = ${p} × ${(th * 3600).toFixed(0)} = ${energyJ.toFixed(4)} J`,
        `Cost = kWh × rate = ${energyKwh.toFixed(4)} × ${rate} = ${cost.toFixed(4)}`,
    ].join('\n')

    return { energy_kwh: energyKwh, energy_j: energyJ, cost, steps }
}

// ============================================================
// 6. Energy-Mass Equivalence Calculator (E = mc^2)
// ============================================================
export function massEnergyEquivalenceCalculator(params: { solve_for: unknown; mass_kg: unknown; energy_j: unknown; language: unknown }): {
    result: string; steps: string
} {
    const solveFor = String(params.solve_for || 'energy')
    const m = Number(params.mass_kg) || 0
    const e = Number(params.energy_j) || 0

    if (solveFor === 'mass') {
        const result = e / (C_LIGHT * C_LIGHT)
        return { result: formatScientific(result, 'kg'), steps: `m = E / c² = ${formatScientific(e, 'J')} / (${C_LIGHT})² = ${formatScientific(result, 'kg')}` }
    }
    const result = m * C_LIGHT * C_LIGHT
    return { result: formatScientific(result, 'J'), steps: `E = mc² = ${m} × (${C_LIGHT})² = ${formatScientific(result, 'J')}` }
}

// ============================================================
// 7. Efficiency Calculator (eta = output / input * 100)
// ============================================================
export function efficiencyCalculator(params: { solve_for: unknown; energy_output_j: unknown; energy_input_j: unknown; efficiency_percent: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'efficiency')
    const output = Number(params.energy_output_j) || 0
    const input = Number(params.energy_input_j) || 0
    const eff = Number(params.efficiency_percent) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'output') {
        const result = (eff / 100) * input
        return { result, steps: `Output = (η/100) × Input = (${eff}/100) × ${input} = ${result.toFixed(4)} J` }
    }
    if (solveFor === 'input') {
        if (eff === 0) return { result: errText, steps: errText }
        const result = output / (eff / 100)
        return { result, steps: `Input = Output / (η/100) = ${output} / (${eff}/100) = ${result.toFixed(4)} J` }
    }
    if (input === 0) return { result: errText, steps: errText }
    const result = (output / input) * 100
    return { result, steps: `η = (Output / Input) × 100 = (${output} / ${input}) × 100 = ${result.toFixed(4)}%` }
}

// ============================================================
// 8. Escape Velocity Calculator (v = sqrt(2GM/r))
// ============================================================
export function escapeVelocityCalculator(params: { mass_kg: unknown; radius_m: unknown; language: unknown }): {
    escape_velocity_ms: number; steps: string
} {
    const m = Math.max(Number(params.mass_kg) || 0, 0)
    const r = Math.max(Number(params.radius_m) || 0.0001, 0.0001)

    const result = Math.sqrt((2 * G * m) / r)
    const steps = `v = √(2GM / r) = √(2 × ${G} × ${formatScientific(m, 'kg')} / ${r}) = ${result.toFixed(4)} m/s`

    return { escape_velocity_ms: result, steps }
}

// ============================================================
// 9. Energy Unit Converter (J, cal, kWh, BTU, eV)
// ============================================================
const ENERGY_UNIT_TO_JOULES: Record<string, number> = {
    j: 1,
    cal: 4.184,
    kwh: 3.6e6,
    btu: 1055.06,
    ev: EV_IN_JOULES,
}

export function energyUnitConverter(params: { value: unknown; from_unit: unknown; to_unit: unknown; language: unknown }): { result: number } {
    const value = Number(params.value) || 0
    const fromUnit = String(params.from_unit || 'j').toLowerCase()
    const toUnit = String(params.to_unit || 'cal').toLowerCase()

    const fromFactor = ENERGY_UNIT_TO_JOULES[fromUnit] ?? 1
    const toFactor = ENERGY_UNIT_TO_JOULES[toUnit] ?? 1

    const joules = value * fromFactor
    const result = joules / toFactor

    return { result }
}

// ============================================================
// 10. Rotational Kinetic Energy Calculator (KE = 1/2 I omega^2)
// ============================================================
export function rotationalKineticEnergyCalculator(params: { solve_for: unknown; moment_of_inertia_kgm2: unknown; angular_velocity_rads: unknown; kinetic_energy_j: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'kinetic_energy')
    const i = Number(params.moment_of_inertia_kgm2) || 0
    const w = Number(params.angular_velocity_rads) || 0
    const ke = Math.max(Number(params.kinetic_energy_j) || 0, 0)
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'moment_of_inertia') {
        if (w === 0) return { result: errText, steps: errText }
        const result = (2 * ke) / (w * w)
        return { result, steps: `I = 2×KE / ω² = 2×${ke} / ${w}² = ${result.toFixed(4)} kg·m²` }
    }
    if (solveFor === 'angular_velocity') {
        if (i <= 0) return { result: errText, steps: errText }
        const result = Math.sqrt((2 * ke) / i)
        return { result, steps: `ω = √(2×KE / I) = √(2×${ke} / ${i}) = ${result.toFixed(4)} rad/s` }
    }
    const result = 0.5 * i * w * w
    return { result, steps: `KE = ½Iω² = 0.5 × ${i} × ${w}² = ${result.toFixed(4)} J` }
}

// ============================================================
// 11. Wind Power Calculator (P = 1/2 rho A v^3)
// ============================================================
export function windPowerCalculator(params: { air_density_kgm3: unknown; swept_area_m2: unknown; wind_speed_ms: unknown; language: unknown }): {
    power_w: number; steps: string
} {
    const rho = Math.max(Number(params.air_density_kgm3) || 0, 0)
    const a = Math.max(Number(params.swept_area_m2) || 0, 0)
    const v = Math.max(Number(params.wind_speed_ms) || 0, 0)

    const result = 0.5 * rho * a * v * v * v
    const steps = `P = ½ρAv³ = 0.5 × ${rho} × ${a} × ${v}³ = ${result.toFixed(4)} W`

    return { power_w: result, steps }
}

// ============================================================
// 12. Photon Energy Calculator (E = hf = hc/lambda)
// ============================================================
export function photonEnergyCalculator(params: { mode: unknown; value: unknown; language: unknown }): {
    energy_j: string; energy_ev: string; steps: string
} {
    const mode = String(params.mode || 'wavelength_nm')
    const value = Math.max(Number(params.value) || 0.0001, 0.0001)

    let energyJ: number
    let steps: string
    if (mode === 'frequency_hz') {
        energyJ = PLANCK_H * value
        steps = `E = hf = ${PLANCK_H} × ${formatScientific(value, 'Hz')} = ${formatScientific(energyJ, 'J')}`
    } else {
        const wavelengthM = value * 1e-9
        energyJ = (PLANCK_H * C_LIGHT) / wavelengthM
        steps = `E = hc / λ = (${PLANCK_H} × ${C_LIGHT}) / ${formatScientific(wavelengthM, 'm')} = ${formatScientific(energyJ, 'J')}`
    }
    const energyEv = energyJ / EV_IN_JOULES

    return { energy_j: formatScientific(energyJ, 'J'), energy_ev: formatScientific(energyEv, 'eV'), steps }
}
